import { Card, Text } from "@overmap-ai/blocks"
import { type IssueAssociation, type Stored, UNLABELLED_ASSET_LABEL } from "@overmap-ai/common"
import { selectAssetById, selectAssetTypeStatusById, selectUserById } from "@overmap-ai/core"
import { memo } from "react"

import { interactiveTextCva } from "../../cva"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { AssetTypeStatusIcon } from "../Icons/AssetTypeStatusIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { AssetLink } from "../Links"
import { TimelineIconRail } from "../TimelineIconRail/TimelineIconRail"
import type { IssueTimelineSize } from "./typings"

interface IssueTimelineIssueAssociationItemProps {
	issueAssociation: Stored<IssueAssociation>
	size: IssueTimelineSize
}

export const IssueTimelineIssueAssociationItem = memo((props: IssueTimelineIssueAssociationItemProps) => {
	const { issueAssociation, size } = props

	const createdBy = useAppSelector(selectUserById(issueAssociation.created_by ?? -1))
	const asset = useAppSelector(selectAssetById(issueAssociation.asset))!
	const assetTypeStatus = useAppSelector(selectAssetTypeStatusById(asset.status))!

	return (
		<div className="flex h-max w-full gap-4">
			<TimelineIconRail icon={modelIconMapping.assetType} size={size} />
			<Card className="flex min-w-0 grow flex-col gap-1" size={size}>
				<Text size={size} accentColor="base">
					<Text highContrast>{getUserDisplayLabel(createdBy)}</Text>
					&nbsp;linked asset
					<Text accentColor="base">
						&nbsp;&#8226;&nbsp;{formatCompactDate(issueAssociation.submitted_at)}
					</Text>
				</Text>

				<Text accentColor="base" className="min-w-0 truncate" size={size}>
					<AssetTypeIcon assetTypeId={asset.asset_type} className="align-sub" />
					&nbsp;
					<AssetLink assetId={asset.uuid} className={interactiveTextCva()}>
						<AssetIndexLabel assetId={asset.uuid} highContrast />
						&nbsp;
						<Text accentColor="base">{asset.label || UNLABELLED_ASSET_LABEL}</Text>
					</AssetLink>
					&nbsp;&nbsp;
					<AssetTypeStatusIcon assetTypeStatusId={asset.status} className="align-sub" />
					&nbsp;
					<Text accentColor="base">{assetTypeStatus.name}</Text>
				</Text>
			</Card>
		</div>
	)
})
IssueTimelineIssueAssociationItem.displayName = "IssueTimelineIssueAssociationItem"
